import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion } from 'framer-motion';
import {
  LayoutGrid, ChefHat, Store, QrCode, CreditCard, Printer, Users,
  ArrowRight, Sparkles, Zap, RefreshCw, ShieldCheck, Check, Send, Loader2,
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Logo } from '../components/common/Logo';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { useTilt } from '../hooks/useTilt';
import tpvImg from '../assets/tpv-vexelpos.png';

const reveal = {
  hidden: { opacity: 0, y: 34, scale: 0.97 },
  show: (i = 0) => ({ opacity: 1, y: 0, scale: 1, transition: { duration: 0.6, delay: (i % 3) * 0.09, ease: [0.22, 1, 0.36, 1] } }),
};

const TiltCard = ({ children, className = '', ...rest }) => {
  const { ref, onMouseMove, onMouseLeave } = useTilt({ max: 8 });
  return (
    <div className="[perspective:1200px]">
      <div
        ref={ref}
        onMouseMove={onMouseMove}
        onMouseLeave={onMouseLeave}
        className={`[transform-style:preserve-3d] transition-[box-shadow,border-color,background-color] duration-200 ${className}`}
        {...rest}
      >
        {children}
      </div>
    </div>
  );
};

export default function Landing({ onEnter }) {
  const { t } = useTranslation();
  const reduce = useReducedMotion();

  const emptyLead = { first_name: '', last_name: '', email: '', business_name: '', business_type: '', business_address: '', phone: '', note: '' };
  const [lead, setLead] = useState(emptyLead);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const setField = (k, v) => setLead((l) => ({ ...l, [k]: v }));

  const bizTypes = [
    { v: 'ristorante', l: t('landing.form.typeRestaurant', { defaultValue: 'Ristorante' }) },
    { v: 'bar', l: t('landing.form.typeBar', { defaultValue: 'Bar' }) },
    { v: 'pizzeria', l: t('landing.form.typePizzeria', { defaultValue: 'Pizzeria' }) },
    { v: 'caffetteria', l: t('landing.form.typeCafe', { defaultValue: 'Caffetteria' }) },
    { v: 'pub', l: t('landing.form.typePub', { defaultValue: 'Pub' }) },
    { v: 'negozio', l: t('landing.form.typeStore', { defaultValue: 'Negozio / Retail' }) },
    { v: 'altro', l: t('landing.form.typeOther', { defaultValue: 'Altro' }) },
  ];

  const scrollToContact = () => {
    const el = document.getElementById('landing-contact');
    if (el) el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!lead.first_name || !lead.last_name || !lead.email || !lead.business_name || !lead.business_type || !lead.business_address) {
      toast.error(t('landing.form.fillAll', { defaultValue: 'Compila tutti i campi.' }));
      return;
    }
    setSending(true);
    try {
      await api.post('/leads', lead);
      setSent(true); setLead(emptyLead);
      toast.success(t('landing.form.success', { defaultValue: 'Richiesta inviata! Ti ricontatteremo presto.' }));
    } catch (err) {
      toast.error(t('landing.form.error', { defaultValue: 'Invio non riuscito. Riprova.' }));
    } finally { setSending(false); }
  };

  const features = [
    { key: 'pos', Icon: LayoutGrid, pill: t('landing.pill.sala', { defaultValue: 'Sala' }), title: t('landing.feat.pos.title', { defaultValue: 'Ordini ai tavoli' }), desc: t('landing.feat.pos.desc', { defaultValue: 'Mappa sala personalizzabile e presa comande fulminea.' }) },
    { key: 'kds', Icon: ChefHat, pill: t('landing.pill.cucina', { defaultValue: 'Cucina' }), title: t('landing.feat.kds.title', { defaultValue: 'Display cucina (KDS)' }), desc: t('landing.feat.kds.desc', { defaultValue: 'Comande smistate a cucina e bar in tempo reale.' }) },
    { key: 'store', Icon: Store, pill: t('landing.pill.retail', { defaultValue: 'Retail' }), title: t('landing.feat.store.title', { defaultValue: 'Vendita negozio' }), desc: t('landing.feat.store.desc', { defaultValue: 'Barcode, catalogo e checkout unificato per il retail.' }) },
    { key: 'menu', Icon: QrCode, pill: t('landing.pill.menu', { defaultValue: 'Menu' }), title: t('landing.feat.menu.title', { defaultValue: 'Carta digitale' }), desc: t('landing.feat.menu.desc', { defaultValue: 'Menu QR multilingua sempre aggiornato.' }) },
    { key: 'pay', Icon: CreditCard, pill: t('landing.pill.pagamenti', { defaultValue: 'Pagamenti' }), title: t('landing.feat.pay.title', { defaultValue: 'Pagamenti e report' }), desc: t('landing.feat.pay.desc', { defaultValue: 'Contanti, carta, conto diviso e report fiscali Z/X.' }) },
    { key: 'print', Icon: Printer, pill: t('landing.pill.stampa', { defaultValue: 'Stampa' }), title: t('landing.feat.print.title', { defaultValue: 'Stampa universale' }), desc: t('landing.feat.print.desc', { defaultValue: 'ESC/POS USB, ePOS e SDP: stampi ovunque.' }) },
    { key: 'roles', Icon: Users, pill: t('landing.pill.team', { defaultValue: 'Team' }), title: t('landing.feat.roles.title', { defaultValue: 'Multi-ruolo e PWA' }), desc: t('landing.feat.roles.desc', { defaultValue: 'Permessi per ruolo, installabile, multilingua.' }) },
  ];

  const values = [
    { Icon: Sparkles, title: t('landing.value1Title', { defaultValue: 'Tutto in uno' }), desc: t('landing.value1Desc', { defaultValue: 'Sala, cucina, cassa, negozio e menu digitale in un\u2019unica app.' }) },
    { Icon: Zap, title: t('landing.value2Title', { defaultValue: 'Velocit\u00e0 reale' }), desc: t('landing.value2Desc', { defaultValue: 'Interfaccia istantanea, ottimizzata per il ritmo del servizio.' }) },
    { Icon: RefreshCw, title: t('landing.value3Title', { defaultValue: 'Sempre sincronizzato' }), desc: t('landing.value3Desc', { defaultValue: 'Ordini, pagamenti e report aggiornati in tempo reale su ogni dispositivo.' }) },
  ];

  const steps = [
    { n: '1', title: t('landing.step1Title', { defaultValue: 'Configura' }), desc: t('landing.step1Desc', { defaultValue: 'Imposta sala, listino e ruoli.' }) },
    { n: '2', title: t('landing.step2Title', { defaultValue: 'Servi' }), desc: t('landing.step2Desc', { defaultValue: 'Prendi ordini e invia in preparazione.' }) },
    { n: '3', title: t('landing.step3Title', { defaultValue: 'Incassa e analizza' }), desc: t('landing.step3Desc', { defaultValue: 'Pagamenti, scontrini e report.' }) },
  ];

  const stats = [
    { v: '6', l: t('landing.stat1', { defaultValue: 'Lingue' }) },
    { v: '100%', l: t('landing.stat2', { defaultValue: 'Cloud sync' }) },
    { v: '24/7', l: t('landing.stat3', { defaultValue: 'Operativo' }) },
    { v: 'PWA', l: t('landing.stat4', { defaultValue: 'Installabile' }) },
  ];

  const scrollToFeatures = () => {
    const el = document.getElementById('landing-features');
    if (el) el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  };

  return (
    <div className="vxp-landing min-h-dvh bg-background text-foreground overflow-x-hidden" data-testid="landing-page">
      {/* Header sticky */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-background/70 border-b border-border">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 h-16 flex items-center justify-between">
          <button className="flex items-center gap-2 outline-none" data-testid="landing-logo-link" onClick={() => window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' })}>
            <Logo className="h-9 w-9" />
            <span className="font-display font-extrabold tracking-tight text-lg">{t('common.appName', { defaultValue: 'VexelPOS' })}</span>
          </button>
          <div className="flex items-center gap-2 sm:gap-3">
            <LanguageSwitcher variant="compact" onChange={(code) => localStorage.setItem('rmp_login_lang', code)} data-testid="landing-language-switch" />
            <Button onClick={onEnter} className="rounded-xl px-4 sm:px-5 shadow-sm-token" data-testid="landing-manager-button">
              {t('landing.ctaManager', { defaultValue: 'VexelPOS Manager' })}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div aria-hidden className="pointer-events-none absolute inset-0" style={{ backgroundImage: 'radial-gradient(900px 420px at 18% 8%, hsl(var(--primary) / 0.20), transparent 60%), radial-gradient(700px 360px at 82% 0%, hsl(var(--accent) / 0.16), transparent 55%)' }} />
        <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, hsl(var(--foreground) / 0.5) 1px, transparent 0)', backgroundSize: '18px 18px' }} />
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 pt-16 sm:pt-20 pb-14 grid lg:grid-cols-2 gap-10 items-center">
          <motion.div initial="hidden" animate="show" variants={reveal}>
            <Badge variant="secondary" className="rounded-full px-3 py-1 text-xs font-semibold" data-testid="landing-hero-badge">
              <Sparkles className="h-3.5 w-3.5 mr-1.5 text-primary" /> {t('landing.badge', { defaultValue: 'Gestionale per ristoranti e negozi' })}
            </Badge>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">
              {t('landing.heroTitle', { defaultValue: 'Il POS intelligente per il tuo locale' })}
            </h1>
            <p className="mt-4 max-w-xl text-base sm:text-lg text-muted-foreground leading-relaxed">
              {t('landing.heroSubtitle', { defaultValue: 'Gestisci tavoli, cucina, cassa e negozio da un\u2019unica piattaforma. Veloce, moderna e sempre sincronizzata.' })}
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Button onClick={scrollToContact} size="lg" className="rounded-xl px-6 h-12 text-base shadow-sm-token" data-testid="landing-hero-primary-cta">
                {t('landing.ctaRequest', { defaultValue: 'Richiedi ora' })}
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
              <Button onClick={scrollToFeatures} variant="outline" size="lg" className="rounded-xl px-6 h-12 text-base" data-testid="landing-hero-secondary-cta">
                {t('landing.ctaDiscover', { defaultValue: 'Scopri le funzioni' })}
              </Button>
            </div>
            <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm text-muted-foreground">
              {[t('landing.trust1', { defaultValue: 'Multilingua' }), t('landing.trust2', { defaultValue: 'PWA installabile' }), t('landing.trust3', { defaultValue: 'Realtime' })].map((x) => (
                <span key={x} className="inline-flex items-center gap-1.5"><Check className="h-4 w-4 text-primary" /> {x}</span>
              ))}
            </div>
          </motion.div>

          {/* Visual: TPV VexelPOS reale — STATICO (nessuna animazione) */}
          <div className="relative flex items-center justify-center min-h-[320px]" data-testid="landing-hero-3d-visual">
            <div aria-hidden className="absolute -inset-8 rounded-full blur-3xl opacity-70"
              style={{ background: 'radial-gradient(closest-side, hsl(var(--primary) / 0.38), transparent)' }} />
            <img
              src={tpvImg} alt="VexelPOS TPV" draggable={false}
              className="relative z-10 w-full max-w-[560px] select-none [filter:drop-shadow(0_24px_40px_rgba(0,0,0,0.28))]" />

            {/* Chip decorativi statici */}
            <div className="absolute z-20 -left-2 top-6 rounded-2xl border border-border bg-card/90 backdrop-blur px-3 py-2 shadow-lg-token">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-primary/15 text-primary flex items-center justify-center"><CreditCard className="h-4 w-4" /></div>
                <div>
                  <div className="text-[10px] text-muted-foreground leading-none">{t('order.total', { defaultValue: 'Totale' })}</div>
                  <div className="font-display font-black tabular text-base leading-tight">€ 15,00</div>
                </div>
              </div>
            </div>
            <div className="absolute z-20 -right-1 top-24 rounded-2xl border border-border bg-card/90 backdrop-blur px-3 py-2 shadow-lg-token">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-accent/15 text-accent flex items-center justify-center"><RefreshCw className="h-4 w-4" /></div>
                <span className="text-xs font-semibold">{t('landing.trust3', { defaultValue: 'Realtime' })}</span>
              </div>
            </div>
            <div className="absolute z-20 left-8 bottom-4 rounded-full border border-border bg-card/90 backdrop-blur px-3 py-1.5 shadow-lg-token">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold"><Printer className="h-4 w-4 text-primary" /> Ticket #000123</span>
            </div>
          </div>
        </div>
      </section>

      {/* Perché è innovativo */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-14">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.25 }} variants={reveal} className="text-center max-w-2xl mx-auto">
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">{t('landing.whyTitle', { defaultValue: 'Perch\u00e9 \u00e8 innovativo' })}</h2>
          <p className="mt-2 text-muted-foreground">{t('landing.whySubtitle', { defaultValue: 'Tutto ci\u00f2 che serve al tuo servizio, senza complicazioni.' })}</p>
        </motion.div>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          {values.map((v, i) => (
            <motion.div key={v.title} custom={i} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.25 }} variants={reveal}>
              <div className="h-full rounded-2xl border border-border bg-card p-6 shadow-sm-token" data-testid="landing-value-card">
                <div className="h-11 w-11 rounded-xl bg-primary/15 text-primary flex items-center justify-center"><v.Icon className="h-5 w-5" /></div>
                <h3 className="mt-4 text-lg font-bold">{v.title}</h3>
                <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{v.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Funzioni */}
      <section id="landing-features" className="relative">
        <div aria-hidden className="pointer-events-none absolute inset-0" style={{ backgroundImage: 'radial-gradient(700px 300px at 85% 20%, hsl(var(--primary) / 0.08), transparent 60%)' }} />
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 py-14">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={reveal} className="text-center max-w-2xl mx-auto">
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">{t('landing.featuresTitle', { defaultValue: 'Funzioni principali' })}</h2>
            <p className="mt-2 text-muted-foreground">{t('landing.featuresSubtitle', { defaultValue: 'Progettato per ristoranti, bar e attivit\u00e0 retail.' })}</p>
          </motion.div>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {features.map((f, i) => (
              <motion.div key={f.key} custom={i} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }} variants={reveal}>
                <TiltCard className="h-full rounded-2xl border border-border bg-card p-6 hover:border-primary hover:shadow-lg-token" data-testid={`landing-feature-card-${f.key}`}>
                  <div className="flex items-center justify-between">
                    <div className="h-11 w-11 rounded-xl bg-primary/15 text-primary flex items-center justify-center [transform:translateZ(18px)]"><f.Icon className="h-5 w-5" /></div>
                    <Badge variant="secondary" className="rounded-full text-[10px] font-bold">{f.pill}</Badge>
                  </div>
                  <h3 className="mt-4 text-lg font-bold">{f.title}</h3>
                  <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
                </TiltCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Come funziona */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-14">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.25 }} variants={reveal} className="text-center max-w-2xl mx-auto">
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">{t('landing.howTitle', { defaultValue: 'Come funziona' })}</h2>
          <p className="mt-2 text-muted-foreground">{t('landing.howSubtitle', { defaultValue: 'Dalla configurazione all\u2019incasso in tre passi.' })}</p>
        </motion.div>
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {steps.map((s, i) => (
            <motion.div key={s.n} custom={i} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.25 }} variants={reveal}>
              <div className="h-full rounded-2xl border border-border bg-card p-6 shadow-sm-token" data-testid={`landing-flow-step-${s.n}`}>
                <div className="h-10 w-10 rounded-full bg-primary text-primary-foreground font-display font-black flex items-center justify-center">{s.n}</div>
                <h3 className="mt-4 text-lg font-bold">{s.title}</h3>
                <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Stats / trust strip */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 pb-14">
        <div className="rounded-2xl border border-border bg-muted/40 px-6 py-6 grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((s) => (
            <div key={s.l} className="text-center" data-testid="landing-stat-item">
              <div className="font-display text-2xl sm:text-3xl font-black text-primary tabular">{s.v}</div>
              <div className="mt-1 text-xs font-mono uppercase tracking-wide text-muted-foreground">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA finale */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 pb-16">
        <div className="relative overflow-hidden rounded-[24px] border border-border bg-card p-8 sm:p-10 text-center">
          <div aria-hidden className="pointer-events-none absolute inset-0" style={{ backgroundImage: 'radial-gradient(600px 260px at 50% 0%, hsl(var(--primary) / 0.16), transparent 60%)' }} />
          <div className="relative">
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">{t('landing.finalTitle', { defaultValue: 'Pronto a iniziare?' })}</h2>
            <p className="mt-2 text-muted-foreground max-w-xl mx-auto">{t('landing.finalSubtitle', { defaultValue: 'Lasciaci i tuoi dati: ti ricontattiamo per attivare VexelPOS nella tua attivit\u00e0.' })}</p>
            <Button onClick={scrollToContact} size="lg" className="mt-6 rounded-xl px-7 h-12 text-base shadow-sm-token" data-testid="landing-final-cta">
              {t('landing.ctaRequest', { defaultValue: 'Richiedi ora' })}
              <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Contatto / Richiedi ora */}
      <section id="landing-contact" className="scroll-mt-24 mx-auto max-w-3xl px-4 sm:px-6 pb-20" data-testid="landing-contact-section">
        <div className="relative overflow-hidden rounded-[24px] border border-border bg-card p-6 sm:p-8 shadow-sm-token">
          <div aria-hidden className="pointer-events-none absolute inset-0" style={{ backgroundImage: 'radial-gradient(560px 240px at 50% 0%, hsl(var(--primary) / 0.10), transparent 60%)' }} />
          {sent ? (
            <div className="relative text-center py-8" data-testid="landing-contact-success">
              <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Check className="h-8 w-8 text-primary" />
              </div>
              <h3 className="font-display text-2xl font-extrabold tracking-tight">{t('landing.form.successTitle', { defaultValue: 'Richiesta inviata!' })}</h3>
              <p className="mt-2 text-muted-foreground max-w-md mx-auto">{t('landing.form.successBody', { defaultValue: 'Grazie! Il nostro team ti ricontatter\u00e0 al pi\u00f9 presto.' })}</p>
              <Button variant="outline" className="mt-6 rounded-xl" onClick={() => setSent(false)} data-testid="landing-contact-again">
                {t('landing.form.again', { defaultValue: 'Invia un\u2019altra richiesta' })}
              </Button>
            </div>
          ) : (
            <div className="relative">
              <div className="text-center max-w-xl mx-auto mb-7">
                <Badge variant="secondary" className="rounded-full px-3 py-1 text-xs font-semibold">
                  <Send className="h-3.5 w-3.5 mr-1.5 text-primary" /> {t('landing.ctaRequest', { defaultValue: 'Richiedi ora' })}
                </Badge>
                <h3 className="mt-3 font-display text-2xl sm:text-3xl font-extrabold tracking-tight">{t('landing.form.title', { defaultValue: 'Richiedi una demo gratuita' })}</h3>
                <p className="mt-2 text-muted-foreground">{t('landing.form.subtitle', { defaultValue: 'Compila il modulo: ti ricontattiamo per attivare VexelPOS nella tua attivit\u00e0.' })}</p>
              </div>

              <form onSubmit={submitLead} className="space-y-4" data-testid="landing-contact-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="lead-first">{t('landing.form.firstName', { defaultValue: 'Nome' })} <span className="text-destructive">*</span></Label>
                    <Input id="lead-first" className="h-11" value={lead.first_name} onChange={(e) => setField('first_name', e.target.value)} data-testid="lead-first-name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="lead-last">{t('landing.form.lastName', { defaultValue: 'Cognome' })} <span className="text-destructive">*</span></Label>
                    <Input id="lead-last" className="h-11" value={lead.last_name} onChange={(e) => setField('last_name', e.target.value)} data-testid="lead-last-name" />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="lead-email">{t('landing.form.email', { defaultValue: 'Email' })} <span className="text-destructive">*</span></Label>
                  <Input id="lead-email" type="email" className="h-11" value={lead.email} onChange={(e) => setField('email', e.target.value)} data-testid="lead-email" />
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="lead-biz">{t('landing.form.businessName', { defaultValue: 'Nome attivit\u00e0' })} <span className="text-destructive">*</span></Label>
                    <Input id="lead-biz" className="h-11" value={lead.business_name} onChange={(e) => setField('business_name', e.target.value)} data-testid="lead-business-name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="lead-type">{t('landing.form.businessType', { defaultValue: 'Tipo attivit\u00e0' })} <span className="text-destructive">*</span></Label>
                    <Select value={lead.business_type} onValueChange={(v) => setField('business_type', v)}>
                      <SelectTrigger id="lead-type" className="h-11" data-testid="lead-business-type">
                        <SelectValue placeholder={t('landing.form.selectType', { defaultValue: 'Seleziona\u2026' })} />
                      </SelectTrigger>
                      <SelectContent>
                        {bizTypes.map((b) => (
                          <SelectItem key={b.v} value={b.v} data-testid={`lead-type-${b.v}`}>{b.l}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="lead-addr">{t('landing.form.address', { defaultValue: 'Indirizzo attivit\u00e0' })} <span className="text-destructive">*</span></Label>
                  <Input id="lead-addr" className="h-11" value={lead.business_address} onChange={(e) => setField('business_address', e.target.value)} data-testid="lead-address" />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="lead-phone">{t('landing.form.phone', { defaultValue: 'Telefono' })} <span className="text-muted-foreground text-xs font-normal">({t('landing.form.optional', { defaultValue: 'facoltativo' })})</span></Label>
                  <Input id="lead-phone" type="tel" className="h-11" value={lead.phone} onChange={(e) => setField('phone', e.target.value)} data-testid="lead-phone" />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="lead-note">{t('landing.form.note', { defaultValue: 'Note' })} <span className="text-muted-foreground text-xs font-normal">({t('landing.form.optional', { defaultValue: 'facoltativo' })})</span></Label>
                  <Textarea id="lead-note" rows={3} value={lead.note} onChange={(e) => setField('note', e.target.value)} data-testid="lead-note" />
                </div>

                <Button type="submit" size="lg" disabled={sending} className="w-full rounded-xl h-12 text-base shadow-sm-token" data-testid="lead-submit">
                  {sending ? (
                    <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> {t('landing.form.sending', { defaultValue: 'Invio in corso\u2026' })}</>
                  ) : (
                    <><Send className="h-5 w-5 mr-2" /> {t('landing.form.submit', { defaultValue: 'Invia richiesta' })}</>
                  )}
                </Button>
              </form>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border" data-testid="landing-footer">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Logo className="h-8 w-8" />
            <span className="font-display font-bold">{t('common.appName', { defaultValue: 'VexelPOS' })}</span>
          </div>
          <p className="text-xs text-muted-foreground text-center">
            © {new Date().getFullYear()} {t('common.appName', { defaultValue: 'VexelPOS' })} · {t('login.rightsReserved', { defaultValue: 'Tutti i diritti riservati' })}
          </p>
          <div className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"><ShieldCheck className="h-4 w-4 text-primary" /> {t('landing.trust2', { defaultValue: 'PWA installabile' })}</div>
        </div>
      </footer>
    </div>
  );
}
